// LayerSLDModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import './LayerSLDModal.css';
import { getLayerAttributes } from '../../../../../../_config/layers/zcm';

const NUMERIC_TYPES = ['double', 'int', 'integer', 'numeric', 'decimal', 'real', 'serial', 'float', 'number'];
const isNumericType = (t = '') => NUMERIC_TYPES.includes(String(t).toLowerCase());

const parseAttributes = (attrStr = '') =>
  String(attrStr)
    .split(',')
    .map(pair => {
      const [name, type] = pair.split(':').map(s => (s || '').trim());
      return name ? { name, type } : null;
    })
    .filter(Boolean);

function toHex(color) {
  if (!color) return '000000';
  if (color.startsWith('#')) return color.replace('#', '').trim();
  const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (m) {
    const r = (+m[1]).toString(16).padStart(2, '0');
    const g = (+m[2]).toString(16).padStart(2, '0');
    const b = (+m[3]).toString(16).padStart(2, '0');
    return `${r}${g}${b}`;
  }
  return color.trim().replace('#', '');
}

function buildFilterXML(attr, op, value, value2) {
  if (op === 'between') {
    const lo = Number(value);
    const hi = Number(value2);
    if (Number.isNaN(lo) || Number.isNaN(hi)) {
      return `
        <ogc:PropertyIsBetween>
          <ogc:PropertyName>${attr}</ogc:PropertyName>
          <ogc:LowerBoundary><ogc:Literal>0</ogc:Literal></ogc:LowerBoundary>
          <ogc:UpperBoundary><ogc:Literal>0</ogc:Literal></ogc:UpperBoundary>
        </ogc:PropertyIsBetween>`.trim();
    }
    return `
      <ogc:PropertyIsBetween>
        <ogc:PropertyName>${attr}</ogc:PropertyName>
        <ogc:LowerBoundary><ogc:Literal>${lo}</ogc:Literal></ogc:LowerBoundary>
        <ogc:UpperBoundary><ogc:Literal>${hi}</ogc:Literal></ogc:UpperBoundary>
      </ogc:PropertyIsBetween>`.trim();
  }

  const opTag = {
    '=': 'PropertyIsEqualTo',
    '>': 'PropertyIsGreaterThan',
    '>=': 'PropertyIsGreaterThanOrEqualTo',
    '<': 'PropertyIsLessThan',
    '<=': 'PropertyIsLessThanOrEqualTo',
  }[op] || 'PropertyIsEqualTo';

  return `
    <ogc:${opTag}>
      <ogc:PropertyName>${attr}</ogc:PropertyName>
      <ogc:Literal>${value}</ogc:Literal>
    </ogc:${opTag}>`.trim();
}

function buildSymbolizerXML(geomType, hex, opacity = 1) {
  const stroke = `
    <Stroke>
      <CssParameter name="stroke">#${hex}</CssParameter>
      <CssParameter name="stroke-width">1.5</CssParameter>
      <CssParameter name="stroke-opacity">${opacity}</CssParameter>
    </Stroke>`.trim();

  if (/point/i.test(geomType)) {
    return `
      <PointSymbolizer>
        <Graphic>
          <Mark>
            <WellKnownName>circle</WellKnownName>
            <Fill>
              <CssParameter name="fill">#${hex}</CssParameter>
              <CssParameter name="fill-opacity">${opacity}</CssParameter>
            </Fill>
            ${stroke}
          </Mark>
          <Size>8</Size>
        </Graphic>
      </PointSymbolizer>`.trim();
  }
  if (/line/i.test(geomType)) {
    return `
      <LineSymbolizer>
        ${stroke}
      </LineSymbolizer>`.trim();
  }
  return `
    <PolygonSymbolizer>
      <Fill>
        <CssParameter name="fill">#${hex}</CssParameter>
        <CssParameter name="fill-opacity">${opacity}</CssParameter>
      </Fill>
      ${stroke}
    </PolygonSymbolizer>`.trim();
}

function buildSLD({ layerName, geomType = 'Polygon', rules = [] }) {
  const rulesXML = rules.map((r, idx) => `
  <Rule>
    <Name>rule_${idx + 1}</Name>
    <Title>${r.attr} ${r.op === 'between' ? `${r.value}–${r.value2}` : `${r.op} ${r.value}`}</Title>
    <ogc:Filter>
      ${buildFilterXML(r.attr, r.op, r.value, r.value2)}
    </ogc:Filter>
    ${buildSymbolizerXML(geomType, toHex(r.color), 0.85)}
  </Rule>
`.trim()).join('');

  return `
  <StyledLayerDescriptor version="1.0.0"
      xmlns="http://www.opengis.net/sld"
      xmlns:ogc="http://www.opengis.net/ogc"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
      xmlns:gml="http://www.opengis.net/gml"
      xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd">
    <NamedLayer>
      <Name>${layerName.split(':')[1]}</Name>
      <UserStyle>
        <Title>SLD on-the-fly</Title>
        <FeatureTypeStyle>
          ${rulesXML}
        </FeatureTypeStyle>
      </UserStyle>
    </NamedLayer>
  </StyledLayerDescriptor>`.trim();
}

export default function LayerSLDModal({ map, layer, wmsName, active, callback }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [rules, setRules] = useState([]);
  const [attr, setAttr] = useState('');
  const [op, setOp] = useState('=');
  const [val, setVal] = useState('');
  const [val2, setVal2] = useState('');
  const [attributes, setAttributes] = useState([]);
  const [geomType, setGeomType] = useState([]);
  const [color, setColor] = useState('#26a737');

  useEffect(() => {
    if (!wmsName) return;
    (async () => {
      try {
        const { wfsAttributes, wfsGeometryType } = await getLayerAttributes(wmsName.split(':')[1]);
        setAttributes(wfsAttributes || '');
        setGeomType(wfsGeometryType || 'Polygon');
      } catch (err) {
        console.error('getLayerAttributes failed:', err);
        setAttributes('');
        setGeomType('Polygon');
      }
    })();
  }, [wmsName]);

  useEffect(() => { if (active) setShow(true); }, [active]);
  useEffect(() => { callback?.(show); }, [show, callback]);
  const handleClose = () => setShow(false);

  const numericAttrs = useMemo(() => {
    const parsed = parseAttributes(attributes || '');
    return parsed.filter(a => isNumericType(a.type));
  }, [attributes]);

  const layerName = useMemo(() => {
    return wmsName || layer?.get?.('wmsName') || layer?.get?.('name') || layer?.get?.('title') || 'layer';
  }, [wmsName, layer]);

  const addRule = () => {
    if (!attr) return;
    if (op === 'between') {
      const n1 = Number(val);
      const n2 = Number(val2);
      if (Number.isNaN(n1) || Number.isNaN(n2)) return;
      const lo = Math.min(n1, n2);
      const hi = Math.max(n1, n2);
      setRules(prev => [...prev, { attr, op, value: lo, value2: hi, color }]);
      return;
    }
    if (val === '' || isNaN(Number(val))) return;
    setRules(prev => [...prev, { attr, op, value: Number(val), color }]);
  };

  const removeRule = (idx) => setRules(prev => prev.filter((_, i) => i !== idx));

  const minifyXML = (xml) =>
    xml.replace(/>\s+</g, '><').replace(/\s{2,}/g, ' ').trim();

  const applySLD = () => {
    if (!layer?.getSource?.() || !rules.length) return;
    const xml = buildSLD({ layerName, geomType, rules });
    const minified = minifyXML(xml);
    const src = layer.getSource();
    src.updateParams?.({
      VERSION: '1.1.0',
      TILED: false,
      SLD_BODY: minified
    });
    src.refresh?.();
  };

  const resetSLD = () => {
    const src = layer?.getSource?.();
    if (!src?.updateParams) return;
    const params = { ...(src.getParams?.() || {}) };
    params.SLD_BODY = null;
    params.SLD = null;
    params.SLD_VERSION = '1.3.0';
    params.LAYERS = wmsName;
    params.STYLES = '';
    params.TILED = true;
    params.tiled = true;
    params._reset = Date.now();
    src.updateParams(params);
    src.refresh?.();
  };

  const footer = (
    <div className="px-4 pb-3 d-flex gap-2 justify-content-end">
      <button className="btn btn-outline-secondary" onClick={resetSLD}>
        {t('sld.reset') || 'Restaurar estilo'}
      </button>
      <button className="btn btn-primary" onClick={applySLD} disabled={!rules.length}>
        {t('sld.apply') || 'Gerar & aplicar SLD'}
      </button>
    </div>
  );

  return (
    <Dialog
      className="LayerSLDModal"
      header={t('sld.title') || 'Editor de Estilo (SLD)'}
      visible={show}
      onHide={handleClose}
      modal
      appendTo={document.body}
      position="right"
      draggable={false}
      resizable={false}
      blockScroll
      style={{ width: '500px', height: '100%' }}
      footer={footer}
    >
      <div className="p-3">
        <div className="mb-3 p-3 border rounded bg-light">
          <div>
            <strong>{t('sld.layer.title')}: </strong> {layer?.get?.('title') || '-'}
          </div>
        </div>

        {/* Criador de regra */}
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label small mb-1">{t('sld.attribute') || 'Atributo (numérico)'}</label>
            <select
              className="form-select form-select-sm"
              value={attr}
              onChange={(e) => setAttr(e.target.value)}
            >
              <option value="">{t('sld.selectAttribute') || 'Selecione'}</option>
              {numericAttrs.map(a => (
                <option key={a.name} value={a.name}>{a.name.replace?.('_', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>

          <div className="col-4 col-md-2">
            <label className="form-label small mb-1">{t('sld.operator') || 'Operador'}</label>
            <select
              className="form-select form-select-sm"
              value={op}
              onChange={(e) => setOp(e.target.value)}
            >
              <option value="=">=</option>
              <option value=">">&gt;</option>
              <option value=">=">&ge;</option>
              <option value="<">&lt;</option>
              <option value="<=">&le;</option>
              <option value="between">between</option>
            </select>
          </div>

          <div className="col-8 col-md-3">
            <label className="form-label small mb-1">
              {op === 'between' ? (t('sld.min') || 'Mín') : (t('sld.value') || 'Valor')}
            </label>
            <input
              type="number"
              step="any"
              className="form-control form-control-sm"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder={op === 'between' ? 'Ex.: 10' : 'Ex.: 10.5'}
            />
          </div>

          {op === 'between' && (
            <div className="col-8 col-md-3">
              <label className="form-label small mb-1">{t('sld.max') || 'Máx'}</label>
              <input
                type="number"
                step="any"
                className="form-control form-control-sm"
                value={val2}
                onChange={(e) => setVal2(e.target.value)}
                placeholder="Ex.: 20"
              />
            </div>
          )}

          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">{t('sld.color') || 'Cor'}</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={color}
              title="Escolha a cor"
              onChange={(e) => setColor(e.target.value)}
            />
          </div>

          <div className="col-6 col-md-1 d-grid">
            <button className="btn btn-success btn-sm" onClick={addRule}>+</button>
          </div>
        </div>

        {/* Lista de regras */}
        <div className="mt-3">
          <h6 className="mb-2">{t('sld.rules') || 'Regras'}</h6>
          {rules.length === 0 && (
            <div className="text-muted small">
              {t('sld.noRules') || 'Nenhuma regra adicionada.'}
            </div>
          )}
          {rules.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>{t('sld.attribute') || 'Atributo'}</th>
                    <th>{t('sld.operator') || 'Operador'}</th>
                    <th>{t('sld.value') || 'Valor'}</th>
                    <th>{t('sld.color') || 'Cor'}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.attr}</td>
                      <td>{r.op}</td>
                      <td>{r.op === 'between' ? `${r.value} – ${r.value2}` : r.value}</td>
                      <td>
                        <span
                          className="d-inline-block rounded"
                          style={{ width: 18, height: 18, background: r.color, border: '1px solid #ddd' }}
                          title={r.color}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeRule(idx)}
                          title={t('sld.remove') || 'Remover'}
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
